<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CommercialCatalogAPI from 'dashboard/api/commercialCatalog';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';

const { t } = useI18n();
const items = ref([]);
const meta = ref({ current_page: 1, total_pages: 1, total_count: 0 });
const isLoading = ref(false);
const isSaving = ref(false);
const isEditorOpen = ref(false);
const filters = reactive({
  q: '',
  category: '',
  family: '',
  brand: '',
  active: '',
});
const form = reactive({
  id: null,
  name: '',
  sku: '',
  description: '',
  category: '',
  family: '',
  brand: '',
  unit: '',
  currency: '',
  base_price: null,
  tax_rate: null,
  active: true,
  metadata: {},
});

const hasItems = computed(() => items.value.length > 0);
const editorTitle = computed(() =>
  form.id
    ? t('COMMERCIAL.CATALOG.EDITOR.EDIT')
    : t('COMMERCIAL.CATALOG.EDITOR.NEW')
);
const fieldDefinitions = computed(() => [
  { key: 'sku', label: t('COMMERCIAL.CATALOG.FIELDS.SKU') },
  { key: 'category', label: t('COMMERCIAL.CATALOG.FIELDS.CATEGORY') },
  { key: 'family', label: t('COMMERCIAL.CATALOG.FIELDS.FAMILY') },
  { key: 'brand', label: t('COMMERCIAL.CATALOG.FIELDS.BRAND') },
  { key: 'unit', label: t('COMMERCIAL.CATALOG.FIELDS.UNIT') },
  { key: 'currency', label: t('COMMERCIAL.CATALOG.FIELDS.CURRENCY') },
]);
const statusLabel = active =>
  active
    ? t('COMMERCIAL.CATALOG.STATUS.ACTIVE')
    : t('COMMERCIAL.CATALOG.STATUS.INACTIVE');
const toggleLabel = active =>
  active
    ? t('COMMERCIAL.CATALOG.ACTIONS.DEACTIVATE')
    : t('COMMERCIAL.CATALOG.ACTIONS.ACTIVATE');
const resetForm = () =>
  Object.assign(form, {
    id: null,
    name: '',
    sku: '',
    description: '',
    category: '',
    family: '',
    brand: '',
    unit: '',
    currency: '',
    base_price: null,
    tax_rate: null,
    active: true,
    metadata: {},
  });
const openCreate = () => {
  resetForm();
  isEditorOpen.value = true;
};
const openEdit = item => {
  Object.assign(form, item);
  isEditorOpen.value = true;
};
const closeEditor = () => {
  isEditorOpen.value = false;
  resetForm();
};
const loadItems = async (page = 1) => {
  isLoading.value = true;
  try {
    const { data } = await CommercialCatalogAPI.list({ ...filters, page });
    items.value = data.payload;
    meta.value = data.meta;
  } catch {
    useAlert(t('COMMERCIAL.CATALOG.MESSAGES.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};
const saveItem = async () => {
  isSaving.value = true;
  try {
    await CommercialCatalogAPI.save({ ...form });
    useAlert(t('COMMERCIAL.CATALOG.MESSAGES.SAVED'));
    closeEditor();
    await loadItems(meta.value.current_page);
  } catch {
    useAlert(t('COMMERCIAL.CATALOG.MESSAGES.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};
const toggleActive = async item => {
  try {
    await CommercialCatalogAPI.save({ id: item.id, active: !item.active });
    await loadItems(meta.value.current_page);
  } catch {
    useAlert(t('COMMERCIAL.CATALOG.MESSAGES.SAVE_ERROR'));
  }
};
const clearFilters = () => {
  Object.assign(filters, {
    q: '',
    category: '',
    family: '',
    brand: '',
    active: '',
  });
  loadItems();
};
const formatPrice = item => {
  if (item.base_price === null) return '—';
  const value = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
  }).format(Number(item.base_price));
  return item.currency ? `${item.currency} ${value}` : value;
};

onMounted(loadItems);
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="t('COMMERCIAL.CATALOG.TITLE')"
        :description="t('COMMERCIAL.CATALOG.DESCRIPTION')"
      >
        <template #actions>
          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white hover:bg-n-brand-hover"
            @click="openCreate"
          >
            <span class="i-lucide-plus size-4" />
            {{ t('COMMERCIAL.CATALOG.NEW') }}
          </button>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="space-y-5">
        <section
          class="grid gap-3 rounded-2xl border border-n-weak bg-n-background p-4 md:grid-cols-2 xl:grid-cols-6"
        >
          <input
            v-model.trim="filters.q"
            :placeholder="t('COMMERCIAL.CATALOG.FILTERS.SEARCH')"
            class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12 xl:col-span-2"
            @keyup.enter="loadItems()"
          />
          <input
            v-model.trim="filters.category"
            :placeholder="t('COMMERCIAL.CATALOG.FIELDS.CATEGORY')"
            class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
          />
          <input
            v-model.trim="filters.family"
            :placeholder="t('COMMERCIAL.CATALOG.FIELDS.FAMILY')"
            class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
          />
          <input
            v-model.trim="filters.brand"
            :placeholder="t('COMMERCIAL.CATALOG.FIELDS.BRAND')"
            class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
          />
          <select
            v-model="filters.active"
            class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
          >
            <option value="">{{ t('COMMERCIAL.CATALOG.FILTERS.ALL') }}</option>
            <option value="true">
              {{ t('COMMERCIAL.CATALOG.STATUS.ACTIVE') }}
            </option>
            <option value="false">
              {{ t('COMMERCIAL.CATALOG.STATUS.INACTIVE') }}
            </option>
          </select>
          <div class="flex gap-2 xl:col-span-6">
            <button
              type="button"
              class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white"
              @click="loadItems()"
            >
              {{ t('COMMERCIAL.CATALOG.FILTERS.APPLY') }}
            </button>
            <button
              type="button"
              class="rounded-lg border border-n-weak px-4 py-2 text-sm text-n-slate-11"
              @click="clearFilters"
            >
              {{ t('COMMERCIAL.CATALOG.FILTERS.CLEAR') }}
            </button>
          </div>
        </section>

        <section
          class="overflow-hidden rounded-2xl border border-n-weak bg-n-background"
        >
          <div
            v-if="!isLoading && !hasItems"
            class="grid min-h-72 place-content-center p-8 text-center"
          >
            <span
              class="i-lucide-package-open mx-auto mb-3 size-10 text-n-slate-7"
            />
            <h3 class="font-medium text-n-slate-12">
              {{ t('COMMERCIAL.CATALOG.EMPTY.TITLE') }}
            </h3>
            <p class="mt-1 max-w-md text-sm text-n-slate-9">
              {{ t('COMMERCIAL.CATALOG.EMPTY.DESCRIPTION') }}
            </p>
          </div>
          <div v-else class="overflow-x-auto">
            <table class="w-full min-w-[840px] text-left text-sm">
              <thead
                class="border-b border-n-weak bg-n-alpha-1 text-xs uppercase text-n-slate-9"
              >
                <tr>
                  <th class="px-4 py-3">
                    {{ t('COMMERCIAL.CATALOG.FIELDS.NAME') }}
                  </th>
                  <th class="px-4 py-3">
                    {{ t('COMMERCIAL.CATALOG.FIELDS.SKU') }}
                  </th>
                  <th class="px-4 py-3">
                    {{ t('COMMERCIAL.CATALOG.FIELDS.CATEGORY') }}
                  </th>
                  <th class="px-4 py-3">
                    {{ t('COMMERCIAL.CATALOG.FIELDS.BRAND') }}
                  </th>
                  <th class="px-4 py-3">
                    {{ t('COMMERCIAL.CATALOG.FIELDS.PRICE') }}
                  </th>
                  <th class="px-4 py-3">
                    {{ t('COMMERCIAL.CATALOG.FIELDS.STATUS') }}
                  </th>
                  <th class="px-4 py-3 text-right">
                    {{ t('COMMERCIAL.CATALOG.ACTIONS.TITLE') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr
                  v-for="item in items"
                  :key="item.id"
                  class="hover:bg-n-alpha-1"
                >
                  <td class="px-4 py-3">
                    <span class="block font-medium text-n-slate-12">{{
                      item.name
                    }}</span
                    ><span class="line-clamp-1 text-xs text-n-slate-9">{{
                      item.description || '—'
                    }}</span>
                  </td>
                  <td class="px-4 py-3 text-n-slate-11">
                    {{ item.sku || '—' }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-11">
                    {{ item.category || '—' }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-11">
                    {{ item.brand || '—' }}
                  </td>
                  <td class="px-4 py-3 font-medium text-n-slate-12">
                    {{ formatPrice(item) }}
                  </td>
                  <td class="px-4 py-3">
                    <span
                      :class="
                        item.active
                          ? 'bg-n-teal-3 text-n-teal-11'
                          : 'bg-n-slate-3 text-n-slate-10'
                      "
                      class="rounded-full px-2 py-1 text-xs"
                      >{{ statusLabel(item.active) }}</span
                    >
                  </td>
                  <td class="px-4 py-3">
                    <div class="flex justify-end gap-2">
                      <button
                        type="button"
                        class="rounded-lg border border-n-weak px-3 py-1.5 text-n-slate-11"
                        @click="openEdit(item)"
                      >
                        {{ t('COMMERCIAL.CATALOG.ACTIONS.EDIT') }}</button
                      ><button
                        type="button"
                        class="rounded-lg border border-n-weak px-3 py-1.5 text-n-slate-11"
                        @click="toggleActive(item)"
                      >
                        {{ toggleLabel(item.active) }}
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-if="meta.total_pages > 1"
            class="flex items-center justify-between border-t border-n-weak px-4 py-3 text-sm text-n-slate-10"
          >
            <span>{{
              t('COMMERCIAL.CATALOG.PAGINATION', {
                current: meta.current_page,
                total: meta.total_pages,
              })
            }}</span>
            <div class="flex gap-2">
              <button
                type="button"
                :disabled="meta.current_page <= 1"
                class="rounded-lg border border-n-weak px-3 py-1.5 disabled:opacity-40"
                @click="loadItems(meta.current_page - 1)"
              >
                {{ t('COMMERCIAL.CATALOG.ACTIONS.PREVIOUS') }}</button
              ><button
                type="button"
                :disabled="meta.current_page >= meta.total_pages"
                class="rounded-lg border border-n-weak px-3 py-1.5 disabled:opacity-40"
                @click="loadItems(meta.current_page + 1)"
              >
                {{ t('COMMERCIAL.CATALOG.ACTIONS.NEXT') }}
              </button>
            </div>
          </div>
        </section>
      </div>

      <div
        v-if="isEditorOpen"
        class="fixed inset-0 z-50 flex items-end justify-center bg-n-slate-12/60 p-0 md:items-center md:p-6"
        @click.self="closeEditor"
      >
        <form
          class="max-h-[92vh] w-full overflow-y-auto rounded-t-2xl border border-n-weak bg-n-background p-5 shadow-2xl md:max-w-3xl md:rounded-2xl"
          @submit.prevent="saveItem"
        >
          <div class="mb-5 flex items-center justify-between">
            <h3 class="text-lg font-semibold text-n-slate-12">
              {{ editorTitle }}
            </h3>
            <button
              type="button"
              class="i-lucide-x size-5 text-n-slate-9"
              :aria-label="t('COMMERCIAL.CATALOG.ACTIONS.CLOSE')"
              @click="closeEditor"
            />
          </div>
          <div class="grid gap-4 md:grid-cols-2">
            <label
              class="flex flex-col gap-1.5 text-sm text-n-slate-11 md:col-span-2"
              >{{ t('COMMERCIAL.CATALOG.FIELDS.NAME')
              }}<input
                v-model.trim="form.name"
                required
                class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
            /></label>
            <label
              v-for="field in fieldDefinitions"
              :key="field.key"
              class="flex flex-col gap-1.5 text-sm text-n-slate-11"
              >{{ field.label
              }}<input
                v-model.trim="form[field.key]"
                class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
            /></label>
            <label class="flex flex-col gap-1.5 text-sm text-n-slate-11"
              >{{ t('COMMERCIAL.CATALOG.FIELDS.BASE_PRICE')
              }}<input
                v-model.number="form.base_price"
                type="number"
                min="0"
                step="0.01"
                class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
            /></label>
            <label class="flex flex-col gap-1.5 text-sm text-n-slate-11"
              >{{ t('COMMERCIAL.CATALOG.FIELDS.TAX_RATE')
              }}<input
                v-model.number="form.tax_rate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
            /></label>
            <label
              class="flex flex-col gap-1.5 text-sm text-n-slate-11 md:col-span-2"
              >{{ t('COMMERCIAL.CATALOG.FIELDS.DESCRIPTION')
              }}<textarea
                v-model.trim="form.description"
                rows="4"
                class="rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-n-slate-12"
              />
            </label>
            <label
              class="flex items-center gap-3 text-sm text-n-slate-11 md:col-span-2"
              ><input
                v-model="form.active"
                type="checkbox"
                class="size-5 rounded border-n-weak text-n-brand"
              />{{ t('COMMERCIAL.CATALOG.FIELDS.ACTIVE') }}</label
            >
          </div>
          <div class="mt-6 flex justify-end gap-2">
            <button
              type="button"
              class="rounded-lg border border-n-weak px-4 py-2 text-sm text-n-slate-11"
              @click="closeEditor"
            >
              {{ t('COMMERCIAL.CATALOG.ACTIONS.CANCEL') }}</button
            ><button
              type="submit"
              :disabled="isSaving"
              class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {{ t('COMMERCIAL.CATALOG.ACTIONS.SAVE') }}
            </button>
          </div>
        </form>
      </div>
    </template>
  </SettingsLayout>
</template>
